import {Tooltip} from '@material-ui/core';
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {
  fuzzyMatchHighlight,
  fuzzyMatchWithMapping,
} from '@wandb/weave/common/util/fuzzyMatch';
import {ID} from '@wandb/weave/common/util/id';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {
  maybePluralize,
  maybePluralizeWord,
} from '@wandb/weave/core/util/string';
import {isEmpty, sortBy as _sortBy, uniq} from 'lodash';
import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {useMutation} from 'react-apollo';
import {Link, useHistory} from 'react-router-dom';
import TimeAgo from 'react-timeago';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Confirm, Dropdown, Form, Image} from 'semantic-ui-react';

import * as Generated from '../generated/graphql';
import {ViewMetadata} from '../graphql/views';
import * as Analytics from '../services/analytics';
import {useReportTableQuery} from '../state/graphql/reportTableQuery';
import {useApolloClient} from '../state/hooks';
import * as ViewerHooks from '../state/viewer/hooks';
import {useViewer} from '../state/viewer/hooks';
import * as ViewApi from '../state/views/api';
import {viewerUsingAdminPrivileges} from '../util/admin';
import {useEffectOnce} from '../util/hooks';
import * as Urls from '../util/urls';
import {InstrumentedCenteredWaveLoader as Loader} from './../components/utility/InstrumentedLoader';
import {EditableLabel} from './elements/EditableLabel';
import MultiSelectTable from './MultiSelectTable';
import {MultiStateCheckbox} from './MultiStateCheckbox';
import {
  DiscardReportDraftAnalyticsOrigin,
  DiscardReportDraftModal,
} from './ReportModals';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {BlankReportPage, CreateReportPromptHeader} from './Reports';

type SortBy = 'displayName' | 'updatedAt' | 'user' | 'createdAt';
type SortFn = (r: ViewMetadata) => any;

const SORT_FUNCTION_BY_NAME: {[s in SortBy]: SortFn} = {
  displayName: r => r.displayName,
  updatedAt: r => -new Date(r.updatedAt).valueOf(),
  createdAt: r => -new Date(r.createdAt).valueOf(),
  user: r => r.user.username,
};

export interface ReportTableProps {
  entityName: string;
  projectName: string;
  runCount: number;
  onCreateReportPrompt: () => void;
  onCreateNewReport: () => void;
}

type ReportTableQueryLoadedData = Exclude<
  ReturnType<typeof useReportTableQuery>,
  {loading: true}
>;

// Report-loading widget.

const DEFAULT_PAGE_SIZE = 2000; // TODO(acrellin): Use sensible default when backend supports filtering.

const ReportTable = (props: ReportTableProps) => {
  const {entityName, projectName} = props;

  const reportTableQuery = useReportTableQuery({
    entityName,
    projectName,
    draftsPageSize: DEFAULT_PAGE_SIZE,
    publishedPageSize: DEFAULT_PAGE_SIZE,
  });
  if (reportTableQuery.loading) {
    return <Loader name="report-table-loader" size="huge" />;
  }

  return <ReportTableInner {...props} reportTableQuery={reportTableQuery} />;
};

const ReportTableInner = (
  props: ReportTableProps & {reportTableQuery: ReportTableQueryLoadedData}
) => {
  const {
    entityName,
    projectName,
    runCount,
    reportTableQuery,
    onCreateReportPrompt,
    onCreateNewReport,
  } = props;

  const viewer = useViewer();
  const isAnonymous = viewer == null;
  const [searchQuery, setSearchQuery] = useState('');
  const [byUser, setByUser] = useState('Anyone');

  const readOnly = viewer == null || reportTableQuery.data?.project.readOnly;
  const refetch = reportTableQuery.refetch;
  const reports = (reportTableQuery.data?.project.reports.edges || []).map(
    e => e.node
  );

  useEffect(() => {
    if (!reportTableQuery.loading) {
      window.analytics?.track('Report Table Viewed', {
        entityName,
        projectName,
        reportCount: reports.length,
      });
    }
    // eslint-disable-next-line
  }, [entityName, projectName, reportTableQuery.loading]);

  const reportDrafts = (reportTableQuery.data?.project.reportDrafts.edges || [])
    .map(e => e.node)
    // TODO: filtering should be done on the backend
    .filter(draft => draft.user.id === viewer?.id);
  const masterDraftMap: {[master: string]: string} = {};
  for (const draft of reportDrafts) {
    if (draft.parentId != null) {
      masterDraftMap[draft.parentId] = draft.id;
    }
  }
  const orphanReportDrafts = reportDrafts.filter(
    draft => draft.parentId == null
  );

  const userNames = _sortBy(uniq(reports.map(r => r.user.username)));
  const userOptions = ['Anyone']
    .concat(userNames)
    .map(u => ({text: u, key: u, value: u}));

  let filteredReports = reports;
  let filteredOrphanReportDrafts = orphanReportDrafts;
  if (!isEmpty(searchQuery)) {
    filteredReports = fuzzyMatchWithMapping(
      filteredReports,
      searchQuery,
      report =>
        [report.displayName, report.user.username, report.project.name].join(
          '|'
        ) // search report name and the report username and report project name
    );
    filteredOrphanReportDrafts = fuzzyMatchWithMapping(
      filteredOrphanReportDrafts,
      searchQuery,
      report =>
        [report.displayName, report.user.username, report.project.name].join(
          '|'
        )
    );
  }
  if (byUser !== 'Anyone') {
    filteredReports = filteredReports.filter(r => r.user.username === byUser);
  }

  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteAllDraftsConfirmModalOpen, setDeleteAllDraftsConfirmModalOpen] =
    useState(false);

  const client = useApolloClient();

  const [sortBy, setSortBy] = useState<SortBy>('updatedAt');
  const [sortReverse, setSortReverse] = useState(false);
  const toggleSort = useCallback(
    (sb: SortBy) => {
      if (sb === sortBy) {
        setSortReverse(prev => !prev);
        return;
      }
      setSortBy(sb);
      setSortReverse(false);
    },
    [sortBy]
  );
  const renderSortIcon = (sortKey: string) => {
    if (sortBy !== sortKey) {
      return null;
    }
    return (
      <Icon
        name={`sort-${sortReverse ? 'descending' : 'ascending'}`}
        className="report-table__sort-icon"
      />
    );
  };
  const [sortDraftBy, setSortDraftBy] = useState<SortBy>('updatedAt');
  const [sortDraftReverse, setSortDraftReverse] = useState(false);
  const toggleSortDraft = useCallback(
    (sb: SortBy) => {
      if (sb === sortDraftBy) {
        setSortDraftReverse(prev => !prev);
        return;
      }
      setSortDraftBy(sb);
      setSortDraftReverse(false);
    },
    [sortDraftBy]
  );
  const renderSortDraftIcon = (sortDraftKey: string) => {
    if (sortDraftBy !== sortDraftKey) {
      return null;
    }
    return (
      <Icon
        name={`sort-${sortReverse ? 'descending' : 'ascending'}`}
        className="report-table__sort-icon"
      />
    );
  };

  const createReport = useCallback(
    (location: string) => {
      window.analytics?.track('Create Report Clicked', {
        location,
      });
      onCreateNewReport();
    },
    [onCreateNewReport]
  );

  const shouldRenderEmpty =
    reports.length === 0 && orphanReportDrafts.length === 0;

  useEffectOnce(() => {
    window.analytics?.track(`Empty state viewed`, {location: `reports`});
  }, shouldRenderEmpty);

  const renderEmpty = shouldRenderEmpty
    ? () => <BlankReportPage createReport={createReport} />
    : undefined;

  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  const [initialRenderCycle, setInitialRenderCycle] = useState(true);

  const handleClickNextPage = useCallback(() => {
    setPage(prev => prev + 1);
    setInitialRenderCycle(false);
  }, [setPage]);

  const handleClickPrevPage = useCallback(() => {
    setPage(prev => prev - 1);
    setInitialRenderCycle(false);
  }, [setPage]);

  const handleSetPageSize = useCallback(
    (size: number) => {
      setPage(0);
      setPageSize(size);
      setInitialRenderCycle(false);
    },
    [setPageSize]
  );

  useEffect(() => {
    if (initialRenderCycle) {
      return;
    }
    const offset = page * pageSize;
    refetch({
      projectName,
      entityName,
      publishedOffset: offset,
      publishedPageSize: pageSize,
    });
  }, [page, pageSize, refetch, projectName, entityName, initialRenderCycle]);

  if (shouldRenderEmpty && runCount >= 5) {
    return (
      <CreateReportPromptHeader
        createReport={createReport}
        entityName={entityName}
        projectName={projectName}
        onCreateReportPrompt={onCreateReportPrompt}
      />
    );
  }

  return (
    <MultiSelectTable
      readOnly={reportTableQuery.data?.project.readOnly ?? true}
      rows={filteredReports}
      draftRows={filteredOrphanReportDrafts}
      sortBy={SORT_FUNCTION_BY_NAME[sortBy]}
      sortReverse={sortReverse}
      sortDraftBy={SORT_FUNCTION_BY_NAME[sortDraftBy]}
      sortDraftReverse={sortDraftReverse}
      renderDialogs={filteredSelectedViews => (
        <>
          <Confirm
            open={deleteConfirmModalOpen}
            onCancel={() => setDeleteConfirmModalOpen(false)}
            header={`${maybePluralizeWord(
              filteredSelectedViews.length,
              'Delete report'
            )}?`}
            content={`Are you sure you want to delete ${maybePluralize(
              filteredSelectedViews.length,
              'report',
              's'
            )}? This cannot be undone.`}
            confirmButton={
              <Button
                variant="destructive"
                size="medium"
                className="p-8"
                onClick={() => {
                  ViewApi.deleteViews(
                    client,
                    filteredSelectedViews.map(v => v.id)
                  ).then(({data}) => {
                    if (
                      !data?.deleteViews?.success &&
                      data?.deleteViews?.pendingDrafts
                    ) {
                      setDeleteAllDraftsConfirmModalOpen(true);
                    } else {
                      refetch();
                    }

                    setDeleteConfirmModalOpen(false);
                  });
                }}>
                Delete
              </Button>
            }
            cancelButton={
              <Button variant="secondary" size="medium" className="mr-10 p-8">
                Cancel
              </Button>
            }
          />
          <Confirm
            open={deleteAllDraftsConfirmModalOpen}
            onCancel={() => setDeleteAllDraftsConfirmModalOpen(false)}
            onConfirm={() => {
              ViewApi.deleteViews(
                client,
                filteredSelectedViews.map(v => v.id),
                true
              ).then(({data}) => {
                refetch();
                setDeleteAllDraftsConfirmModalOpen(false);
              });
            }}
            header="Delete Reports"
            content={`Reports have pending draft edits. Are you sure you want to delete them and all their drafts? This cannot be undone.`}
            confirmButton="Yes, delete these reports and all their drafts"
          />
        </>
      )}
      renderDraftHeader={(checked, onClick) => (
        <>
          <div className="report-table__cell report-table__cell--select">
            <MultiStateCheckbox checked={checked} onClick={onClick} />
          </div>
          <div
            className="report-table__cell report-table__table-title"
            onClick={() => toggleSortDraft('displayName')}>
            Your private drafts
            {renderSortDraftIcon('displayName')}
          </div>
          <div
            className="report-table__cell"
            onClick={() => toggleSortDraft('createdAt')}>
            Created On {renderSortDraftIcon('createdAt')}
          </div>
          <div
            className="report-table__cell"
            onClick={() => toggleSortDraft('updatedAt')}>
            Last edited {renderSortDraftIcon('updatedAt')}
          </div>
          <div className="report-table__cell" />
        </>
      )}
      renderDraftRow={
        orphanReportDrafts.length > 0
          ? (r, selected, setSelected) => (
              <ReportTableDraftRow
                key={r.id}
                entityName={entityName}
                report={r}
                searchQuery={searchQuery}
                selected={selected}
                setSelected={setSelected}
                refetch={refetch}
                readOnly={readOnly ?? true}
              />
            )
          : undefined
      }
      renderHeader={(checked, onClick) => (
        <>
          <div className="report-table__cell report-table__cell--select">
            <MultiStateCheckbox checked={checked} onClick={onClick} />
          </div>
          <div
            className="report-table__cell report-table__table-title"
            onClick={() => toggleSort('displayName')}>
            Reports
            {renderSortIcon('displayName')}
          </div>
          <div className="report-table__cell" />
          <div
            className="report-table__cell"
            onClick={() => toggleSort('user')}>
            Created by
            {renderSortIcon('user')}
          </div>
          <div
            className="report-table__cell"
            onClick={() => toggleSort('createdAt')}>
            Created On
            {renderSortIcon('createdAt')}
          </div>
          <div
            className="report-table__cell"
            onClick={() => toggleSort('updatedAt')}>
            Last edited
            {renderSortIcon('updatedAt')}
          </div>
          <div className="report-table__cell" />
        </>
      )}
      renderRow={(r, selected, setSelected) => (
        <ReportTableRow
          key={r.id}
          entityName={entityName}
          report={r}
          searchQuery={searchQuery}
          selected={selected}
          setSelected={setSelected}
          readOnly={readOnly ?? true}
          draftId={masterDraftMap[r.id]}
          refetch={refetch}
        />
      )}
      renderAddButton={
        isAnonymous
          ? undefined
          : () => (
              <Button
                data-test="create-report-table-button"
                variant="primary"
                size="medium"
                onClick={() => {
                  createReport('reports tab');
                }}>
                Create report
              </Button>
            )
      }
      renderDeleteButton={() => (
        <Button
          variant="destructive"
          size="medium"
          className="delete-reports mr-10"
          onClick={() => setDeleteConfirmModalOpen(true)}>
          Delete reports
        </Button>
      )}
      renderSearch={() => (
        <Form.Input
          className="report-table__search"
          value={searchQuery}
          onChange={(e, {value}) => setSearchQuery(value)}
          icon={
            <LegacyWBIcon
              name="search"
              size="large"
              style={{marginRight: -8}}
            />
          }
          placeholder="Search reports..."
        />
      )}
      renderFilter={() => (
        <Tooltip
          title="Only applies to published reports"
          placement="right-end">
          <Form.Dropdown
            className="report-table__user-filter"
            selection
            options={userOptions}
            value={byUser}
            onChange={(e, {value}) => setByUser(value as string)}
          />
        </Tooltip>
      )}
      renderEmpty={renderEmpty}
    />
  );
};

export default ReportTable;

interface ReportTableDraftRowProps {
  entityName: string;
  searchQuery: string;
  report: ViewMetadata;
  readOnly: boolean;
  selected: boolean;
  setSelected(id: string, selected: boolean): void;
  refetch(): void;
}

const ReportTableDraftRow = ({
  entityName,
  report,
  searchQuery,
  selected,
  setSelected,
  refetch,
  readOnly,
}: ReportTableDraftRowProps) => {
  const [name, setName] = useState(report.displayName || '(Untitled)');
  const history = useHistory();
  const editUrl = Urls.reportEdit(
    {
      entityName,
      projectName: report.project.name,
      reportID: report.id,
      reportName: name,
    },
    `?draftId=${report.id}`
  );
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const client = useApolloClient();

  const onSelectChanged = useCallback(
    () => setSelected(report.id, !selected),
    [report.id, selected, setSelected]
  );

  return (
    <>
      <DiscardReportDraftModal
        isModalOpen={deleteConfirmModalOpen}
        view={report}
        draftId={report.id}
        analyticsOrigin={DiscardReportDraftAnalyticsOrigin.ReportTable}
        refetch={refetch}
        onModalClose={() => setDeleteConfirmModalOpen(false)}
      />
      <div className="report-table__row" key={report.id}>
        <div className="report-table__cell report-table__cell--select">
          <MultiStateCheckbox
            checked={selected ? 'checked' : 'unchecked'}
            onClick={onSelectChanged}
          />
        </div>
        <div
          data-test="report-table-row-title"
          className="report-table__cell report-table__cell--title">
          <Link to={editUrl}>
            {!editingName ? (
              <span className="report-table__report-title">
                {fuzzyMatchHighlight(name, searchQuery)}
              </span>
            ) : (
              <EditableLabel
                key={report.id}
                title={name}
                onSave={(newName: string) => {
                  ViewApi.save(client, {
                    id: report.id,
                    displayName: newName,
                  });
                  setName(newName);
                }}
                onBlur={() => setEditingName(false)}
                editing={editingName}
                serverText={name}
                onClick={() => false} // we use the menu option to trigger editing mode, rather than direct click
              />
            )}
          </Link>
          {report.description !== '' && (
            <div className="report-table__description">
              {report.description}
            </div>
          )}
        </div>
        <div className="report-table__cell">
          {report.createdAt
            ? new Date(report.createdAt).toLocaleDateString('sv-SE')
            : ''}
        </div>
        <div className="report-table__cell">
          <TimeAgo date={report.updatedAt} live={false} />
        </div>
        <div className="report-table__cell">
          <Dropdown
            className="dropdown-menu-icon-button"
            floating
            pointing="top right"
            trigger={
              <LegacyWBIcon
                className="report-table__action-button"
                name="overflow"
                onClick={(e: React.SyntheticEvent) => {
                  e.preventDefault();
                }}
              />
            }>
            <Dropdown.Menu>
              <Dropdown.Item
                icon={<LegacyWBIcon name="edit" />}
                content="Rename draft"
                onClick={(e: React.SyntheticEvent) => {
                  setEditingName(true);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              />
              <Dropdown.Item
                icon={<LegacyWBIcon name="copy" />}
                content="Clone draft"
                onClick={() => {
                  Analytics.reportCloneClicked({
                    reportType: 'report draft',
                    location: 'report table',
                  });
                  ViewApi.loadSingleViewById(
                    client,
                    report.id,
                    'no-cache'
                  ).then(loaded => {
                    ViewApi.save(client, {
                      type: 'runs/draft',
                      name: ID(12),
                      displayName: `Copy of ${loaded.displayName}`,
                      description: loaded.description,
                      spec: loaded.spec,
                      project: loaded.project,
                      previewUrl: loaded.previewUrl,
                    }).then(newDraft => {
                      history.push(
                        loaded.project != null
                          ? Urls.reportEdit(
                              {
                                entityName: loaded.project.entityName,
                                projectName: loaded.project.name,
                                reportID: newDraft.id as string,
                                reportName: newDraft.displayName,
                              },
                              `?draftId=${newDraft.id}`
                            )
                          : Urls.galleryPostEdit({
                              entityName: loaded.entityName,
                              reportID: newDraft.id as string,
                              reportName: newDraft.displayName,
                            })
                      );
                    });
                  });
                }}
              />
              <Dropdown.Item
                icon={<LegacyWBIcon name="delete" />}
                content="Discard draft"
                disabled={readOnly}
                onClick={(e: React.SyntheticEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setDeleteConfirmModalOpen(true);
                }}
              />
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </>
  );
};

interface ReportTableRowProps {
  entityName: string;
  searchQuery: string;
  report: ViewMetadata;
  readOnly: boolean;
  selected: boolean;
  draftId?: string;
  setSelected(id: string, selected: boolean): void;
  refetch(): void;
}

const ReportTableRow = (props: ReportTableRowProps) => {
  const {
    entityName,
    report,
    selected,
    searchQuery,
    draftId,
    setSelected,
    refetch,
  } = props;
  const viewer = ViewerHooks.useViewer();
  const [name, setName] = useState(report.displayName || '(Untitled)');
  const history = useHistory();
  const viewUrl = Urls.reportView({
    entityName,
    projectName: report.project.name,
    reportID: report.id,
    reportName: name,
  });
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteAllDraftsConfirmModalOpen, setDeleteAllDraftsConfirmModalOpen] =
    useState(false);
  const [deleteDraftConfirmModalOpen, setDeleteDraftConfirmModalOpen] =
    useState(false);
  const [editingName, setEditingName] = useState(false);
  const client = useApolloClient();
  const [starView] = useMutation<
    Generated.StarViewMutationResult,
    Generated.StarViewMutationVariables
  >(Generated.StarViewDocument);
  const [unstarView] = useMutation<
    Generated.UnstarViewMutationResult,
    Generated.UnstarViewMutationVariables
  >(Generated.UnstarViewDocument);

  const onSelectChanged = useCallback(
    () => setSelected(report.id, !selected),
    [report.id, selected, setSelected]
  );

  const canEdit =
    ((viewer != null && report.user.id === viewer.id) ||
      !report.locked ||
      (viewer?.admin && viewerUsingAdminPrivileges())) &&
    !props.readOnly;

  return (
    <>
      <Confirm
        open={deleteConfirmModalOpen}
        onCancel={() => setDeleteConfirmModalOpen(false)}
        onConfirm={() => {
          ViewApi.deleteView(client, report.id).then(({data}) => {
            if (!data?.deleteView?.success && data?.deleteView?.pendingDrafts) {
              setDeleteAllDraftsConfirmModalOpen(true);
            } else {
              refetch();
            }

            setDeleteConfirmModalOpen(false);
          });
        }}
        header="Delete Report"
        content={`Are you sure you want to delete "${name}"? This cannot be undone!`}
        confirmButton="Yes, delete this report"
      />
      <Confirm
        open={deleteAllDraftsConfirmModalOpen}
        onCancel={() => setDeleteAllDraftsConfirmModalOpen(false)}
        onConfirm={() => {
          ViewApi.deleteView(client, report.id, true).then(({data}) => {
            refetch();
            setDeleteAllDraftsConfirmModalOpen(false);
          });
        }}
        header="Delete Report"
        content={`Report "${name}" has pending draft edits. Are you sure you want to delete it and all its drafts? This cannot be undone!`}
        confirmButton="Yes, delete this report and all its drafts"
      />
      {draftId != null && (
        <DiscardReportDraftModal
          isModalOpen={deleteDraftConfirmModalOpen}
          view={report}
          draftId={draftId}
          analyticsOrigin={DiscardReportDraftAnalyticsOrigin.ReportTable}
          refetch={refetch}
          onModalClose={() => setDeleteDraftConfirmModalOpen(false)}
        />
      )}
      <div className="report-table__row" key={report.id}>
        <div className="report-table__cell report-table__cell--select">
          <MultiStateCheckbox
            checked={selected ? 'checked' : 'unchecked'}
            onClick={onSelectChanged}
          />
        </div>
        <div className="report-table__cell report-table__cell--title">
          <Link to={viewUrl} data-test="report-table-row-title">
            {!editingName ? (
              <span className="report-table__report-title">
                {fuzzyMatchHighlight(name, searchQuery)}
              </span>
            ) : (
              <EditableLabel
                key={report.id}
                title={name}
                onSave={(newName: string) => {
                  ViewApi.save(client, {
                    id: report.id,
                    displayName: newName,
                  });
                  setName(newName);
                }}
                onBlur={() => setEditingName(false)}
                editing={editingName}
                serverText={name}
                onClick={() => false} // we use the menu option to trigger editing mode, rather than direct click
              />
            )}
          </Link>
          {report.description !== '' && (
            <div className="report-table__description">
              {report.description}
            </div>
          )}
        </div>
        <div className="report-table__cell report-table__cell--stars-cell">
          <Icon
            onClick={() => {
              if (report.starred) {
                unstarView({
                  variables: {
                    id: report.id,
                  },
                });
              } else {
                starView({
                  variables: {
                    id: report.id,
                  },
                });
              }
            }}
            name={report.starred ? 'star-filled' : 'star'}
            style={{cursor: 'pointer'}}
          />
          {report.starCount}
        </div>
        <div className="report-table__cell">
          <Link to={Urls.profilePage(report.user.username)}>
            <Image
              src={report.user.photoUrl}
              avatar
              onError={(i: any) => (i.target.style.display = 'none')}
            />
            {report.user.username}
          </Link>
        </div>
        <div className="report-table__cell">
          {report.createdAt
            ? new Date(report.createdAt).toLocaleDateString('sv-SE')
            : ''}
        </div>
        <div className="report-table__cell">
          <TimeAgo date={report.updatedAt} live={false} />
        </div>
        {props.draftId == null ? (
          <div className="report-table__cell">
            {canEdit && (
              <LegacyWBIcon
                onClick={() => {
                  ViewApi.loadSingleViewById(
                    client,
                    report.id,
                    'no-cache'
                  ).then(loaded => {
                    ViewApi.save(client, {
                      type: 'runs/draft',
                      name: ID(12),
                      displayName: loaded.displayName,
                      description: loaded.description,
                      spec: loaded.spec,
                      previewUrl: loaded.previewUrl,
                      coverUrl: loaded.coverUrl,
                      project: {
                        entityName,
                        name: loaded.project?.name,
                      },
                      parentId: loaded.id,
                    }).then(newDraft => {
                      history.push(
                        Urls.reportEdit(
                          {
                            entityName,
                            projectName: report.project.name,
                            reportID: report.id as string,
                            reportName: newDraft.displayName,
                          },
                          `?draftId=${newDraft.id}`
                        )
                      );
                    });
                  });
                }}
                className="report-table__action-button"
                name="edit"
              />
            )}
            <Dropdown
              className="dropdown-menu-icon-button"
              pointing="top right"
              trigger={
                <LegacyWBIcon
                  className="report-table__action-button"
                  name="overflow"
                  onClick={(e: React.SyntheticEvent) => {
                    e.preventDefault();
                  }}
                />
              }>
              <Dropdown.Menu>
                {canEdit && (
                  <Dropdown.Item
                    icon={<LegacyWBIcon name="edit" />}
                    content="Rename report"
                    onClick={(e: React.SyntheticEvent) => {
                      setEditingName(true);
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                )}
                <Dropdown.Item
                  icon={<LegacyWBIcon name="copy" />}
                  content="Clone report"
                  onClick={() => {
                    Analytics.reportCloneClicked({
                      reportType: 'published report',
                      location: 'report table',
                    });
                    ViewApi.loadSingleViewById(
                      client,
                      report.id,
                      'no-cache'
                    ).then(loaded => {
                      ViewApi.save(client, {
                        type: 'runs/draft',
                        name: ID(12),
                        displayName: `Copy of ${loaded.user.username}'s ${loaded.displayName}`,
                        description: loaded.description,
                        spec: loaded.spec,
                        project: loaded.project,
                        previewUrl: loaded.previewUrl,
                        coverUrl: loaded.coverUrl,
                      }).then(newDraft => {
                        history.push(
                          loaded.project != null
                            ? Urls.reportEdit(
                                {
                                  entityName: loaded.project.entityName,
                                  projectName: loaded.project.name,
                                  reportID: newDraft.id as string,
                                  reportName: newDraft.displayName,
                                },
                                `?draftId=${newDraft.id}`
                              )
                            : Urls.galleryPostEdit({
                                entityName: loaded.entityName,
                                reportID: newDraft.id as string,
                                reportName: newDraft.displayName,
                              })
                        );
                      });
                    });
                  }}
                />
                {canEdit && (
                  <Dropdown.Item
                    icon={<LegacyWBIcon name="delete" />}
                    content="Delete report"
                    onClick={(e: React.SyntheticEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteConfirmModalOpen(true);
                    }}
                  />
                )}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        ) : (
          <div className="report-table__cell report-table__cell--child-draft-wrapper">
            <div className="report-table__child-draft">
              <div className="report-table__child-draft-inner">
                <div className="report-table__child-draft-info">
                  Draft edit in progress
                </div>
                <div className="report-table__child-draft-options">
                  <Link
                    className="report-table__child-draft-option"
                    to={Urls.reportEdit(
                      {
                        entityName,
                        projectName: report.project.name,
                        reportName: name,
                        reportID: report.id,
                      },
                      `?draftId=${props.draftId}`
                    )}>
                    Continue
                  </Link>
                  <span className="report-table__child-draft-option-separator">
                    &#8226;
                  </span>
                  <span
                    className="report-table__child-draft-option"
                    onClick={() => {
                      setDeleteDraftConfirmModalOpen(true);
                    }}>
                    Discard
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
